import type { GenerationSseEvent } from "../../shared/types.js";

export type PromptContextDebugPayload =
  | {
      kind: "refine";
      generationId: string;
      presetId: string;
      history: Array<{ position: number; kind: string; content: string }>;
      currentInstruction: string;
    }
  | {
      kind: "create";
      generationId: string;
      source: "typed" | "random";
      instruction: string;
    };

export function formatGenerationSseDebugMessage(event: GenerationSseEvent): string {
  if (event.type === "prompt") {
    return `prompt (${event.source}): ${event.prompt}`;
  }
  if ("message" in event && typeof event.message === "string") {
    return `${event.type}: ${event.message}`;
  }
  return event.type;
}

export function parsePromptContextDebugMessage(
  message: string,
): PromptContextDebugPayload | null {
  const refinePrefix = "refine_prompt_context ";
  const createPrefix = "create_prompt_context ";
  let jsonText: string | null = null;
  let kind: "refine" | "create" | null = null;

  if (message.startsWith(refinePrefix)) {
    jsonText = message.slice(refinePrefix.length);
    kind = "refine";
  } else if (message.startsWith(createPrefix)) {
    jsonText = message.slice(createPrefix.length);
    kind = "create";
  } else {
    return null;
  }

  try {
    const parsed = JSON.parse(jsonText) as Record<string, unknown>;
    if (kind === "refine") {
      const history = Array.isArray(parsed.history)
        ? parsed.history.flatMap((entry, index) => {
            if (!entry || typeof entry !== "object") return [];
            const record = entry as Record<string, unknown>;
            if (typeof record.content !== "string") return [];
            return [
              {
                position: typeof record.position === "number" ? record.position : index + 1,
                kind: typeof record.kind === "string" ? record.kind : "unknown",
                content: record.content,
              },
            ];
          })
        : [];
      return {
        kind: "refine",
        generationId: String(parsed.generationId ?? ""),
        presetId: String(parsed.presetId ?? ""),
        history,
        currentInstruction:
          typeof parsed.currentInstruction === "string" ? parsed.currentInstruction : "",
      };
    }

    return {
      kind: "create",
      generationId: String(parsed.generationId ?? ""),
      source: parsed.source === "random" ? "random" : "typed",
      instruction: typeof parsed.instruction === "string" ? parsed.instruction : "",
    };
  } catch {
    return null;
  }
}

export function formatPromptContextDebugLines(payload: PromptContextDebugPayload): string[] {
  if (payload.kind === "create") {
    return [`create (${payload.source}): ${payload.instruction}`];
  }

  const lines = payload.history.map(
    (entry) => `history[${entry.position}] ${entry.kind}: ${entry.content}`,
  );
  if (payload.currentInstruction) {
    lines.push(`current: ${payload.currentInstruction}`);
  }
  return lines;
}
